import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useContent } from "@/lib/hooks";
import { ENTITIES } from "@/admin/entityConfigs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";

const LANG_TABS = [
  { code: "tr", label: "Türkçe" },
  { code: "en", label: "English" },
  { code: "ru", label: "Русский" },
  { code: "ar", label: "العربية" },
];

const inputCls =
  "h-10 w-full rounded-lg border border-zinc-300 bg-white px-3.5 text-sm focus:border-[#1A4BFF] focus:outline-none";
const textareaCls =
  "w-full rounded-lg border border-zinc-300 bg-white px-3.5 py-2.5 text-sm focus:border-[#1A4BFF] focus:outline-none";

function MultiLangField({ field, value, onChange }) {
  const v = value || {};
  const isLines = field.type === "ml-lines";
  const isArea = field.type === "ml-textarea" || isLines;
  const rows = field.rows || (isLines ? 5 : 4);

  const getVal = (code) => {
    const raw = v[code];
    if (isLines) return Array.isArray(raw) ? raw.join("\n") : raw || "";
    return raw || "";
  };

  const setVal = (code, text) => {
    onChange({ ...v, [code]: isLines ? text.split("\n").filter((l) => l.trim()) : text });
  };

  return (
    <Tabs defaultValue="tr">
      <TabsList className="h-8">
        {LANG_TABS.map((l) => (
          <TabsTrigger key={l.code} value={l.code} className="px-3 py-1 text-xs">
            {l.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {LANG_TABS.map((l) => (
        <TabsContent key={l.code} value={l.code} className="mt-2">
          {isArea ? (
            <textarea
              data-testid={`field-${field.key}-${l.code}`}
              rows={rows}
              dir={l.code === "ar" ? "rtl" : "ltr"}
              value={getVal(l.code)}
              onChange={(e) => setVal(l.code, e.target.value)}
              className={textareaCls}
            />
          ) : (
            <input
              data-testid={`field-${field.key}-${l.code}`}
              dir={l.code === "ar" ? "rtl" : "ltr"}
              value={getVal(l.code)}
              onChange={(e) => setVal(l.code, e.target.value)}
              className={inputCls}
            />
          )}
        </TabsContent>
      ))}
    </Tabs>
  );
}

function FieldInput({ field, value, onChange }) {
  if (field.type.startsWith("ml-")) {
    return <MultiLangField field={field} value={value} onChange={onChange} />;
  }
  if (field.type === "boolean") {
    return (
      <Switch data-testid={`field-${field.key}`} checked={!!value} onCheckedChange={onChange} />
    );
  }
  if (field.type === "select") {
    return (
      <select data-testid={`field-${field.key}`} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls}>
        {field.options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    );
  }
  return (
    <input
      data-testid={`field-${field.key}`}
      type={field.type === "number" ? "number" : "text"}
      step="any"
      value={value ?? ""}
      onChange={(e) => onChange(field.type === "number" ? Number(e.target.value) : e.target.value)}
      className={inputCls}
    />
  );
}

export default function ContentManager() {
  const { coll } = useParams();
  const config = ENTITIES[coll];
  const queryClient = useQueryClient();
  const { data: items = [], isLoading } = useContent(`/admin/content/${coll}`);

  const [editing, setEditing] = useState(null); // null | {} (new) | item
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEditing(null);
  }, [coll]);

  if (!config) return <p className="text-zinc-500">Geçersiz bölüm.</p>;

  const openNew = () => {
    setForm({ ...config.defaults });
    setEditing({});
  };

  const openEdit = (item) => {
    setForm({ ...item });
    setEditing(item);
  };

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: [`/admin/content/${coll}`] });
    queryClient.invalidateQueries({ queryKey: [`/content/${coll}`] });
    queryClient.invalidateQueries({ queryKey: ["/admin/stats"] });
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing?.id) {
        await api.put(`/admin/content/${coll}/${editing.id}`, form);
        toast.success(`${config.singular} güncellendi`);
      } else {
        await api.post(`/admin/content/${coll}`, form);
        toast.success(`${config.singular} eklendi`);
      }
      setEditing(null);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`"${config.nameOf(item)}" silinsin mi?`)) return;
    try {
      await api.delete(`/admin/content/${coll}/${item.id}`);
      toast.success("Silindi");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid={`admin-content-${coll}`}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-black tracking-tight text-zinc-950">{config.title}</h1>
          <p className="mt-1 text-sm text-zinc-500">{items.length} kayıt</p>
        </div>
        <button
          data-testid="admin-add-button"
          onClick={openNew}
          className="inline-flex items-center gap-2 rounded-lg bg-[#1A4BFF] px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#0033CC]"
        >
          <Plus className="h-4 w-4" />
          Yeni {config.singular}
        </button>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-zinc-200 bg-white">
        {isLoading ? (
          <p className="p-6 text-sm text-zinc-500">Yükleniyor...</p>
        ) : items.length === 0 ? (
          <p className="p-6 text-sm text-zinc-500">Henüz kayıt yok.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-zinc-200 bg-zinc-50 text-start text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-5 py-3 text-start font-bold">Ad</th>
                <th className="px-5 py-3 text-start font-bold">Durum</th>
                <th className="px-5 py-3 text-end font-bold">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const isActive = item.active ?? item.published;
                return (
                  <tr key={item.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`admin-row-${item.id}`}>
                    <td className="px-5 py-3.5 font-medium text-zinc-900">{config.nameOf(item)}</td>
                    <td className="px-5 py-3.5">
                      <span
                        className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${
                          isActive ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"
                        }`}
                      >
                        {isActive ? "Aktif" : "Pasif"}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          data-testid={`admin-edit-${item.id}`}
                          onClick={() => openEdit(item)}
                          className="rounded-lg border border-zinc-200 p-2 text-zinc-600 transition-colors hover:border-[#1A4BFF] hover:text-[#1A4BFF]"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          data-testid={`admin-delete-${item.id}`}
                          onClick={() => remove(item)}
                          className="rounded-lg border border-zinc-200 p-2 text-zinc-600 transition-colors hover:border-red-400 hover:text-red-500"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editing?.id ? `${config.singular} Düzenle` : `Yeni ${config.singular}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            {config.fields.map((field) => (
              <div key={field.key}>
                <label className="mb-1.5 block text-sm font-bold text-zinc-700">{field.label}</label>
                <FieldInput
                  field={field}
                  value={form[field.key]}
                  onChange={(val) => setForm((f) => ({ ...f, [field.key]: val }))}
                />
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-end gap-3">
            <button
              onClick={() => setEditing(null)}
              className="rounded-lg border border-zinc-300 px-5 py-2.5 text-sm font-bold text-zinc-700 transition-colors hover:bg-zinc-50"
            >
              İptal
            </button>
            <button
              data-testid="admin-save-button"
              onClick={save}
              disabled={saving}
              className="rounded-lg bg-[#1A4BFF] px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#0033CC] disabled:opacity-60"
            >
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
